import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentIdea } from '@/types/dashboard';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, Eye, ChevronLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';

interface CorkBoardViewProps {
  ideas: ContentIdea[];
  onIdeaClick: (idea: ContentIdea) => void;
  expandedCardId?: string | null;
  setExpandedCardId?: (id: string | null) => void;
  isTrashView?: boolean;
  onTrashAction?: (ideaId: string, source: 'scraped' | 'user_generated') => void;
}

const ROTATION_CLASSES = [
  'rotate-[-2deg]',
  'rotate-[1deg]',
  'rotate-[-1deg]',
  'rotate-[2deg]',
];

const IDEAS_PER_PAGE = 10;


const getPaperColor = (score: number | null, index: number = 0) => {
  const viralScore = score || 0;
  const normalizedScore = Math.max(0, Math.min(100, viralScore));
  const hue = (normalizedScore / 100) * 120; // 0 = red (0°), 100 = green (120°)
  
  // Add very subtle variation based on index
  const hueVariation = (index % 5) * 2 - 4; // -4, -2, 0, 2, 4
  const saturationVariation = (index % 3) * 3 - 3; // -3, 0, 3
  const lightnessVariation = (index % 4) - 1.5; // -1.5, -0.5, 0.5, 1.5
  
  const finalHue = Math.max(0, Math.min(120, hue + hueVariation));
  const saturation = Math.max(55, Math.min(65, 60 + saturationVariation));
  const lightness = Math.max(93, Math.min(97, 95 + lightnessVariation));
  
  return {
    light: `hsl(${finalHue}, ${saturation}%, ${lightness}%)`,
    dark: `hsl(${finalHue}, ${saturation - 20}%, 15%)`,
    borderLight: `hsl(${finalHue}, ${saturation}%, ${lightness - 15}%)`,
    borderDark: `hsl(${finalHue}, ${saturation - 20}%, 30%)`
  };
};

export const CorkBoardView = ({ ideas, onIdeaClick, isTrashView, onTrashAction }: CorkBoardViewProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(ideas.length / IDEAS_PER_PAGE);
  const startIndex = (currentPage - 1) * IDEAS_PER_PAGE;
  const endIndex = startIndex + IDEAS_PER_PAGE;
  const currentIdeas = ideas.slice(startIndex, endIndex);

  const getViralScoreColor = (score: number | null) => {
    if (!score) return 'bg-muted text-muted-foreground';
    if (score >= 70) return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-500/30';
    if (score >= 40) return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30';
    return 'bg-gray-500/20 text-gray-700 dark:text-gray-400 border-gray-500/30';
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="w-full">
      
      {/* Cork Board Background */}
      <div className="relative w-full p-3 sm:p-6 md:p-8 rounded-2xl bg-gradient-to-br from-amber-100/50 via-orange-50/30 to-amber-100/50 dark:from-amber-950/20 dark:via-orange-950/10 dark:to-amber-950/20 border-2 border-amber-200/50 dark:border-amber-800/30 shadow-xl">
        {/* Cork texture overlay */}
        <div className="absolute inset-0 opacity-10 dark:opacity-5 rounded-2xl" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
        
        {/* Empty State */}
        {currentIdeas.length === 0 ? (
          <div className="relative py-20 text-center space-y-4">
            <p className="text-muted-foreground text-lg">
              Przygotuj pierwszy scenariusz w Kreatorze, lub poczekaj na nowe pomysły.
            </p>
            <Button 
              onClick={() => navigate('/kreator-scenariusza')}
              className="gap-2"
            >
              <Sparkles className="h-4 w-4" />
              Kreator scenariuszy
            </Button>
          </div>
        ) : (
        <>
        {/* Papers Grid */}
        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
          {currentIdeas.map((idea, index) => {
            const rotationClass = ROTATION_CLASSES[index % ROTATION_CLASSES.length];
            const scoreForColor = idea.source === 'user_generated' || idea.viral_score == null ? 90 : idea.viral_score;
            const colors = getPaperColor(scoreForColor, index);
            
            return (
              <div
                key={idea.id}
                className={cn(
                  "relative group cursor-pointer transition-all duration-300 hover:scale-105 hover:rotate-0 hover:z-10",
                  rotationClass
                )}
                onClick={() => onIdeaClick(idea)}
              >
                {/* Pin */}
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-6 h-6 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg border-2 border-red-700/50 group-hover:scale-110 transition-transform" />
                
                {/* Paper */}
                <Card 
                  className={cn(
                    "relative p-4 sm:p-6 min-h-[260px] sm:min-h-[280px] flex flex-col border-2",
                    "group-hover:shadow-[8px_8px_24px_rgba(0,0,0,0.25)]",
                    "transition-all duration-300"
                  )}
                  style={{
                    backgroundColor: isDark ? colors.dark : colors.light,
                    borderColor: isDark ? colors.borderDark : colors.borderLight
                  }}
                >
                  {/* Viral Score Badge */}
                  {(idea).source !== 'user_generated' && (
                    <div className="flex items-center justify-between mb-4">
                      {typeof idea.viral_score === 'number' && idea.viral_score === 0 ? (
                        <Badge
                          variant="outline"
                          className="gap-1 font-medium text-muted-foreground border-muted-foreground/30"
                        >
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Analizujemy...
                        </Badge>
                      ) : typeof idea.viral_score === 'number' && idea.viral_score > 0 ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            "gap-1 font-semibold",
                            getViralScoreColor(idea.viral_score)
                          )}
                        >
                          <Star className="h-3 w-3 fill-current" />
                          <span className="tabular-nums">{idea.viral_score}/100</span>
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="gap-1 font-medium text-muted-foreground/70 border-border/30"
                        >
                          <Star className="h-3 w-3 opacity-50" />
                          Trwa obliczanie
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  {/* Title */}
                  <h3 className="text-base sm:text-base font-bold text-foreground mb-2 sm:mb-3 break-words group-hover:text-primary transition-colors min-h-[2.5rem] sm:min-h-[3rem]">
                    {idea.title}
                  </h3>
                  
                  {/* Description with fade effect */}
                  <div className="relative mb-3 sm:mb-4 flex-grow overflow-hidden">
                    <p className="text-sm sm:text-sm text-muted-foreground line-clamp-3 sm:line-clamp-4">
                      {idea.description || idea.content || 'No description available'}
                    </p>
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-current to-transparent pointer-events-none" style={{ color: isDark ? colors.dark : colors.light }} />
                  </div>
                  
                  {/* See More Button */}
                  <button 
                    className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 bg-background hover:bg-muted border border-border/50 text-foreground group-hover:scale-105"
                  >
                    {t('library.seeMore')}
                  </button>
                </Card>
              </div>
            );
          })}
        </div>
        </>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="relative mt-8 flex items-center justify-center gap-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-background hover:bg-muted border border-border/50 text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </button>
            
            <span className="px-4 py-2 text-sm font-medium rounded-full bg-background border border-border/50 text-foreground">
              Strona {currentPage} z {totalPages}
            </span>
            
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed bg-background hover:bg-muted border border-border/50 text-foreground"
            >
              Następna
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
